/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Config } from '../interfaces/Config';
import { startsWith } from 'utils/collection';

const ADS_PREFIX = 'ads:';
const ADS_SESSION_KEY = 'ads-persist-config';

/**
 * Interface for the config object
 */
export interface ConfigObject {
  reset: (configObj: Config) => void;
  get: (key: keyof Config, fallback?: any) => any;
  getBoolean: (key: keyof Config, fallback?: boolean) => boolean;
  getNumber: (key: keyof Config, fallback?: number) => number;
  set: (key: keyof Config, value: any) => void;
}

/**
 * Class representing an ADS config
 * @implements {ConfigObject}
 * @hidden
 */
class ConfigObjectClass implements ConfigObject {
  private m = new Map<keyof Config, any>();

  reset(configObj: Config): void {
    this.m = new Map<keyof Config, any>(Object.entries(configObj) as any);
  }

  get(key: keyof Config, fallback?: any): any {
    const value = this.m.get(key);
    return value !== undefined ? value : fallback;
  }

  getBoolean(key: keyof Config, fallback = false): boolean {
    const val = this.m.get(key);
    if (val === undefined) {
      return fallback;
    }
    if (typeof val === 'string') {
      return val === 'true';
    }
    return !!val;
  }

  getNumber(key: keyof Config, fallback?: number): number {
    const val = parseFloat(this.m.get(key));
    return isNaN(val) ? (fallback !== undefined ? fallback : NaN) : val;
  }

  set(key: keyof Config, value: any): void {
    this.m.set(key, value);
  }
}

export const config = /*@__PURE__*/ new ConfigObjectClass();

/**
 * Save a config object
 * @param   {Window}  win   The window object
 * @param   {Config}  c     The config object
 * @hidden
 */
export const saveConfig = (win: Window, c: Config): void => {
  try {
    win.sessionStorage.setItem(ADS_SESSION_KEY, JSON.stringify(c));
  } catch (err) {
    return;
  }
};

/**
 * Return a config object from a session
 * @param   {Window}  win   The window object
 * @returns {Config}        The config object
 * @hidden
 */
export const configFromSession = (win: Window): Config => {
  try {
    const configStr = win.sessionStorage.getItem(ADS_SESSION_KEY);
    return configStr !== null ? JSON.parse(configStr) : {};
  } catch (err) {
    return {};
  }
};

/**
 * Return a config object from a given url
 * @param   {Window}  win   The window object
 * @returns {Config}        The config object
 * @hidden
 */
export const configFromURL = (win: Window): Config => {
  const configObj: any = {};
  win.location.search
    .slice(1)
    .split('&')
    .map((entry) => entry.split('='))
    .map(([key, value]) => [decodeURIComponent(key), decodeURIComponent(value)])
    .filter(([key]) => startsWith(key, ADS_PREFIX))
    .map(([key, value]) => [key.slice(ADS_PREFIX.length), value])
    .forEach(([key, value]) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      configObj[key] = value;
    });

  return configObj;
};
